import { Skeleton } from "@/common/components/ui/skeleton";
import { Loader2 } from "lucide-react";

export default function LoadingState() {
  return (
    <div className="container mx-auto py-8 px-4 max-w-[1000px] bg-white">
      {/* Header Skeleton */}
      <div className="flex items-center justify-between pb-4 mb-6 border-b border-gray-200 md:mb-8 md:pb-6">
        <Skeleton className="rounded-full h-14 w-14 md:h-28 md:w-28" />
        <Skeleton className="rounded-full h-14 w-14 md:h-28 md:w-28" />
      </div>

      {/* Title Skeleton */}
      <div className="mb-6 text-center md:mb-8">
        <Skeleton className="w-64 h-8 mx-auto" />
      </div>

      {/* Student Profile Skeleton */}
      <div className="p-4 mb-6 border border-blue-100 rounded-lg md:p-6 md:mb-8 bg-blue-50/30">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <Skeleton className="w-16 h-16 rounded-full md:h-20 md:w-20" />
            <div className="flex-1 space-y-2">
              <Skeleton className="w-48 h-6" />
              <Skeleton className="w-32 h-4" />
              <div className="flex gap-2 mt-2">
                <Skeleton className="w-20 h-4" />
                <Skeleton className="w-24 h-4" />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Skeleton className="w-24 h-8 rounded-full" />
            <Skeleton className="w-32 h-8 rounded-full" />
          </div>
        </div>
      </div>

      {/* Tests Overview Skeleton */}
      <div className="mb-6 md:mb-8">
        <div className="flex items-center gap-2 pb-2 mb-4">
          <div className="w-1.5 h-6 bg-blue-600 rounded-full"></div>
          <Skeleton className="w-40 h-6" />
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="p-3 border rounded-lg md:p-4">
              <Skeleton className="w-24 h-4 mb-2" />
              <Skeleton className="w-16 h-8" />
            </div>
          ))}
        </div>

        {/* Progress bar skeleton */}
        <div className="p-3 mt-4 border rounded-lg">
          <div className="flex justify-between mb-1">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="w-12 h-4" />
          </div>
          <Skeleton className="h-2.5 w-full rounded-full" />
          <Skeleton className="w-full h-4 mt-2" />
        </div>
      </div>

      {/* Subject Level Accuracy Skeleton */}
      <div className="container flex flex-col p-3 mx-auto mb-6 border rounded-lg md:p-4 md:mb-8">
        <Skeleton className="w-48 h-6 mb-4" />
        <Skeleton className="w-full h-4 mb-4" />

        {[1, 2, 3].map((i) => (
          <div key={i} className="px-3 py-2 mb-2 rounded bg-gray-50">
            <div className="flex items-center justify-between">
              <Skeleton className="w-32 h-5" />
              <div className="flex items-center w-1/2">
                <Skeleton className="flex-1 h-4 mr-2 rounded-full" />
                <Skeleton className="w-10 h-4" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Course Progress Skeleton */}
      <div className="mb-8">
        <div className="flex items-center gap-2 pb-2 mb-4">
          <div className="w-1.5 h-6 bg-blue-600 rounded-full"></div>
          <Skeleton className="w-40 h-6" />
        </div>

        {[1, 2].map((i) => (
          <div key={i} className="p-3 mb-4 border rounded-lg shadow-sm md:p-4">
            <div className="space-y-2 md:space-y-3">
              <div className="flex flex-col justify-between gap-2 md:flex-row md:items-center">
                <Skeleton className="w-48 h-6" />
                <div className="flex gap-6">
                  <div>
                    <Skeleton className="w-10 h-4 mb-1" />
                    <Skeleton className="w-12 h-6" />
                  </div>
                  <div>
                    <Skeleton className="w-20 h-4 mb-1" />
                    <Skeleton className="w-12 h-6" />
                  </div>
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <Skeleton className="w-16 h-4" />
                  <Skeleton className="w-12 h-4" />
                </div>
                <Skeleton className="w-full h-2 rounded-full" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Loading indicator at the bottom */}
      <div className="flex items-center justify-center p-4 text-center text-blue-700">
        <Loader2 className="w-6 h-6 mr-2 animate-spin" />
        <span className="text-sm font-medium">
          Loading student report data...
        </span>
      </div>
    </div>
  );
}
